import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Btn } from '../components/Btn.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { Field, PasswordInput } from '../components/Field.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { AuthSplitLayout, AuthNotice } from '../components/AuthSplitLayout.tsx';
import { setAdminAccessToken } from '../lib/auth/adminToken.ts';
import { getAdminSupabase } from '../lib/supabase.ts';
import { MIN_PASSWORD_LENGTH, isPasswordValid } from '@l2u/shared/password';
import { PasswordRequirements } from '../components/PasswordRequirements.tsx';
import { friendlyAuthError } from '../lib/auth/friendlyAuthError.ts';

type Flow = 'invite' | 'recovery';

export function AdminAcceptInvite() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<'redeeming' | 'set-password'>('redeeming');
  const [flow, setFlow] = useState<Flow>('invite');
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    let supabase;
    try {
      supabase = getAdminSupabase();
    } catch (err) {
      setError(err);
      return;
    }

    const hash = window.location.hash.replace(/^#/, '');
    const fragment = hash ? new URLSearchParams(hash) : null;
    const query = new URLSearchParams(window.location.search);

    const access = fragment?.get('access_token');
    const refresh = fragment?.get('refresh_token');
    const code = query.get('code');
    const tokenHash = query.get('token_hash');
    const type = (fragment?.get('type') ?? query.get('type') ?? '').toLowerCase();

    if (type === 'recovery') setFlow('recovery');

    const redeem = async (): Promise<string | null | undefined> => {
      if (access && refresh) {
        const { data, error: sessionErr } = await supabase.auth.setSession({
          access_token: access,
          refresh_token: refresh,
        });
        if (sessionErr) throw sessionErr;
        setAdminAccessToken(access);
        return data.user?.email ?? null;
      }
      if (code) {
        const { data, error: exchangeErr } = await supabase.auth.exchangeCodeForSession(code);
        if (exchangeErr) throw exchangeErr;
        setAdminAccessToken(data.session?.access_token ?? null);
        return data.user?.email ?? null;
      }
      if (tokenHash) {
        const otpType = type === 'recovery' ? 'recovery' : 'invite';
        const { data, error: otpErr } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: otpType,
        });
        if (otpErr) throw otpErr;
        setAdminAccessToken(data.session?.access_token ?? null);
        return data.user?.email ?? null;
      }
      throw new Error('Link is missing the credentials needed to continue.');
    };

    void redeem()
      .then((resolvedEmail) => {
        window.history.replaceState({}, '', '/admin/accept-invite');
        setEmail(resolvedEmail ?? null);
        setPhase('set-password');
      })
      .catch((err) => setError(err));
  }, []);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(new Error(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`));
      return;
    }
    if (password !== confirm) {
      setError(new Error('Passwords do not match.'));
      return;
    }
    setSubmitting(true);
    try {
      const supabase = getAdminSupabase();
      const { error: updateErr } = await supabase.auth.updateUser({ password });
      if (updateErr) throw updateErr;
      navigate('/admin', { replace: true });
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  const title = flow === 'recovery' ? 'Reset your password' : 'Welcome';
  const subtitle =
    flow === 'recovery'
      ? 'Enter a new password to finish resetting your account.'
      : 'Set a password to finish setting up your admin account.';
  const submitLabel =
    flow === 'recovery' ? 'Update password and sign in' : 'Set password and sign in';

  if (phase === 'redeeming') {
    if (error) {
      return (
        <AuthNotice variant="admin" title="This link didn’t work" error={error}>
          It may have expired or already been used.
        </AuthNotice>
      );
    }
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50">
        <CenteredSpinner />
        <p className="text-sm text-slate-500">Preparing your account…</p>
      </div>
    );
  }

  return (
    <AuthSplitLayout variant="admin">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1.5">
          <h2 className="text-2xl text-slate-900">{title}</h2>
          <p className="text-sm text-slate-500">{subtitle}</p>
          {email && <p className="text-sm font-medium text-slate-700">{email}</p>}
        </div>

        <form className="flex flex-col gap-5" onSubmit={onSubmit}>
          <Field label="New password" required hint={`At least ${MIN_PASSWORD_LENGTH} characters`}>
            <PasswordInput
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={MIN_PASSWORD_LENGTH}
              className="h-12"
              style={{ borderRadius: 10 }}
            />
          </Field>
          <Field label="Confirm password" required>
            <PasswordInput
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={MIN_PASSWORD_LENGTH}
              className="h-12"
              style={{ borderRadius: 10 }}
            />
          </Field>
          <ErrorMessage error={error} />
          <Btn
            type="submit"
            loading={submitting}
            disabled={!isPasswordValid(password) || password !== confirm}
            className="w-full"
            style={{ borderRadius: 10, height: 48 }}
          >
            {submitLabel}
          </Btn>
          <PasswordRequirements password={password} />
        </form>
      </div>
    </AuthSplitLayout>
  );
}
